import React, { useState, useEffect } from 'react';
import {
  BanknotesIcon,
  BuildingOffice2Icon,
  ShieldCheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

const PROMOS = [
  {
    id: 'savings',
    title: 'Premium Saver',
    description: 'Earn more on your savings with our best rate.',
    cta: 'View rate',
    icon: BanknotesIcon,
    gradient: 'from-emerald-500 to-teal-600',
  },
  {
    id: 'mortgage',
    title: 'Mortgage rates',
    description: 'Compare deals and see how much you could borrow.',
    cta: 'Check eligibility',
    icon: BuildingOffice2Icon,
    gradient: 'from-primary-500 to-primary-700',
  },
  {
    id: 'insurance',
    title: 'Home & travel cover',
    description: 'Protect what matters with our insurance range.',
    cta: 'Get a quote',
    icon: ShieldCheckIcon,
    gradient: 'from-slate-700 to-slate-900',
  },
];

const PROMO_MODAL_CONTENT = {
  savings: {
    title: 'Premium Saver Account',
    subtitle: 'Our best rate for your savings',
    points: [
      '4.25% APY (variable) on balances from $1',
      'No monthly fees – easy access when you need it',
      'NCUA insured up to $250,000',
      'Open and manage your account in the app',
    ],
    cta: 'Apply now',
    disclaimer: 'Rates correct as of today. Terms apply.',
  },
  mortgage: {
    title: 'Mortgage deals',
    subtitle: 'Find a rate that works for you',
    points: [
      'Compare fixed and variable rates from our panel of lenders',
      'See how much you could borrow with our quick calculator',
      'Book a call with a mortgage adviser at no extra cost',
      'First-time buyer and remortgage options available',
    ],
    cta: 'Check eligibility',
    disclaimer: 'Your home may be repossessed if you do not keep up repayments.',
  },
  insurance: {
    title: 'Home & travel insurance',
    subtitle: 'Protection for what matters',
    points: [
      'Buildings and contents cover from trusted underwriters',
      'Single-trip and annual travel policies',
      'Optional add-ons: gadgets, legal expenses, home emergency',
      'Get a quote in minutes – no obligation',
    ],
    cta: 'Get a quote',
    disclaimer: 'Cover subject to terms and conditions.',
  },
};

const LOAD_DURATION_MS = 1800;

function PromoModal({ promoId, onClose }) {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const content = promoId ? PROMO_MODAL_CONTENT[promoId] : null;
  const promo = promoId ? PROMOS.find((p) => p.id === promoId) : null;

  const handleCtaClick = () => setLoading(true);

  useEffect(() => {
    if (!loading) return;
    const timer = setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
    }, LOAD_DURATION_MS);
    return () => clearTimeout(timer);
  }, [loading]);

  if (!content || !promo) return null;

  const Icon = promo.icon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="promo-modal-title">
      <div className="absolute inset-0 bg-slate-900/60 dark:bg-slate-950/80 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative w-full max-w-md rounded-2xl bg-white dark:bg-slate-800 shadow-xl border border-slate-200 dark:border-slate-600 overflow-hidden animate-fadeIn">
        <div className={`bg-gradient-to-br ${promo.gradient} px-6 py-5`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <Icon className="w-7 h-7 text-white" />
              </div>
              <div>
                <h2 id="promo-modal-title" className="text-xl font-bold text-white">{content.title}</h2>
                <p className="text-white/90 text-sm mt-0.5">{content.subtitle}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="shrink-0 p-2 rounded-lg text-white/80 hover:bg-white/20 hover:text-white transition-colors"
              aria-label="Close"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="px-6 py-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-8">
              <div className="w-12 h-12 rounded-full border-2 border-primary-600 dark:border-primary-500 border-t-transparent animate-spin" aria-hidden />
              <p className="mt-4 text-sm font-medium text-slate-700 dark:text-slate-300">Sending your request...</p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">We’re connecting you with our team</p>
            </div>
          ) : submitted ? (
            <>
              <p className="text-slate-700 dark:text-slate-200 font-medium">
                Thanks for your interest. Your banker will be in touch with you shortly.
              </p>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                We’ll contact you at the email or phone number on your account to discuss next steps.
              </p>
              <button
                type="button"
                onClick={onClose}
                className="mt-6 w-full rounded-xl bg-primary-600 px-4 py-3 text-sm font-semibold text-white hover:bg-primary-700 transition-colors"
              >
                Close
              </button>
            </>
          ) : (
            <>
              <ul className="space-y-3">
                {content.points.map((point, i) => (
                  <li key={i} className="flex gap-3 text-sm text-slate-700 dark:text-slate-300">
                    <span className="shrink-0 w-5 h-5 rounded-full bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400 flex items-center justify-center text-xs font-bold">✓</span>
                    {point}
                  </li>
                ))}
              </ul>
              <p className="mt-4 text-xs text-slate-500 dark:text-slate-400">{content.disclaimer}</p>
              <div className="mt-6 flex gap-3">
                <button
                  type="button"
                  onClick={handleCtaClick}
                  className="flex-1 rounded-xl bg-primary-600 px-4 py-3 text-sm font-semibold text-white hover:bg-primary-700 transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
                  disabled={loading}
                >
                  {content.cta}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-3 text-sm font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 transition-colors"
                  disabled={loading}
                >
                  Maybe later
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PromoCard() {
  const [openPromoId, setOpenPromoId] = useState(null);

  return (
    <div aria-label="Offers and promotions">
      <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-3">Offers for you</h3>
      <div className="space-y-3">
        {PROMOS.map((promo) => {
          const Icon = promo.icon;
          return (
            <button
              key={promo.id}
              type="button"
              onClick={() => setOpenPromoId(promo.id)}
              className="group w-full text-left rounded-xl border border-slate-200/80 dark:border-slate-600 bg-white dark:bg-slate-800 p-4 shadow-sm hover:shadow-md hover:border-slate-300/80 dark:hover:border-slate-500 transition-all focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:ring-offset-2 dark:focus:ring-offset-slate-800"
            >
              <div className="flex gap-3">
                <div className={`shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br ${promo.gradient} flex items-center justify-center`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-slate-900 dark:text-slate-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">{promo.title}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{promo.description}</p>
                  <span className="inline-block mt-2 text-xs font-medium text-primary-600 dark:text-primary-400 group-hover:underline">{promo.cta}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <PromoModal promoId={openPromoId} onClose={() => setOpenPromoId(null)} />
    </div>
  );
}
